import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '@/utils/apiUrl';

const AuthContext = createContext(null);

const BACKEND_URL = API_BASE_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('authToken'));

  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('authToken');
      if (savedToken) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          if (response.data.success) {
            setUser(response.data.user);
            setToken(savedToken);
            
            // CRITICAL: Do NOT overwrite numerologyData here!
            // This allows "Skaičiuoti iš naujo" to work for logged-in users
            // The user's profile data is stored in the `user` object, not localStorage
            
            // Set VIP status
            if (response.data.user.isVIP) {
              localStorage.setItem('isVIP', 'true');
              localStorage.setItem('vipEmail', response.data.user.email);
              localStorage.setItem('vipPlan', response.data.user.vipPlan || 'monthly');
            }
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('authToken');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    console.log('[AUTH DEBUG] Login attempt started');
    console.log('[AUTH DEBUG] BACKEND_URL:', BACKEND_URL);
    console.log('[AUTH DEBUG] Email:', email);
    
    try {
      console.log('[AUTH DEBUG] Sending request to:', `${BACKEND_URL}/api/auth/login`);
      
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password
      });
      
      console.log('[AUTH DEBUG] Response received:', response.status);
      console.log('[AUTH DEBUG] Response data:', JSON.stringify(response.data, null, 2));
      
      if (response.data.success) {
        const { token: newToken, user: userData } = response.data;
        
        console.log('[AUTH DEBUG] Login successful, saving token...');
        
        try {
          localStorage.setItem('authToken', newToken);
          console.log('[AUTH DEBUG] Token saved to localStorage');
        } catch (storageError) {
          console.error('[AUTH DEBUG] localStorage write ERROR:', storageError);
          return { success: false, error: 'Nepavyko išsaugoti sesijos. Patikrinkite naršyklės nustatymus.' };
        }
        
        setToken(newToken);
        setUser(userData);
        
        // CRITICAL: Do NOT overwrite numerologyData on login!
        // This preserves any "Skaičiuoti iš naujo" calculations
        
        // Set VIP status
        if (userData.isVIP) {
          localStorage.setItem('isVIP', 'true');
          localStorage.setItem('vipEmail', userData.email);
          localStorage.setItem('vipPlan', userData.vipPlan || 'monthly');
          console.log('[AUTH DEBUG] VIP status saved');
        }
        
        console.log('[AUTH DEBUG] Login complete, returning success');
        return { success: true };
      } else {
        console.log('[AUTH DEBUG] Response success was false:', response.data);
        return { success: false, error: response.data.message || 'Prisijungimo klaida' };
      }
    } catch (error) {
      console.error('[AUTH DEBUG] Login ERROR:', error);
      console.error('[AUTH DEBUG] Error name:', error.name);
      console.error('[AUTH DEBUG] Error message:', error.message);
      console.error('[AUTH DEBUG] Error response status:', error.response?.status);
      console.error('[AUTH DEBUG] Error response data:', error.response?.data);
      
      // Provide specific error messages based on error type
      let errorMessage;
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        errorMessage = 'Nepavyko prisijungti prie serverio. Patikrinkite interneto ryšį.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Neteisingas el. paštas arba slaptažodis';
      } else if (error.response?.status === 500) {
        errorMessage = 'Serverio klaida. Bandykite vėliau.';
      } else if (error.response?.status === 0 || error.message?.includes('CORS')) {
        errorMessage = 'Prieigos klaida (CORS). Susisiekite su palaikymo komanda.';
      } else {
        errorMessage = `Prisijungimo klaida: ${error.message || 'Nežinoma klaida'}`;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, userData);
      
      if (response.data.success) {
        const { token: newToken, user: newUser } = response.data;
        localStorage.setItem('authToken', newToken);
        setToken(newToken);
        setUser(newUser);
        
        // CRITICAL: Set numerologyData from registration data so ResultsPage has data to display
        const numerologyData = {
          firstName: userData.firstName,
          lastName: userData.lastName,
          day: userData.day,
          month: userData.month,
          year: userData.year,
          lifePath: newUser.lifePath,
          gender: userData.gender || null
        };
        localStorage.setItem('numerologyData', JSON.stringify(numerologyData));
        
        // Clear guest calculation count since user is now registered
        localStorage.removeItem('guestCalculations');
        
        return { success: true };
      }
    } catch (error) {
      let errorMessage;
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        errorMessage = 'Nepavyko prisijungti prie serverio. Patikrinkite interneto ryšį.';
      } else {
        errorMessage = `Registracijos klaida: ${error.message || 'Nežinoma klaida'}`;
      }
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = () => {
    // Clear all auth tokens
    localStorage.removeItem('authToken');
    localStorage.removeItem('isVIP');
    localStorage.removeItem('vipEmail');
    localStorage.removeItem('vipPlan');
    // Clear user data completely
    localStorage.removeItem('numerologyData');
    localStorage.removeItem('partnerData');
    // Clear birth date info modal flag so new users see it again
    localStorage.removeItem('numenaxis_birthdate_info_seen');
    // Reset state
    setToken(null);
    setUser(null);
    // Navigate to home page (will be done by component using this)
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setUser(response.data.user);
          
          // Update VIP status
          if (response.data.user.isVIP) {
            localStorage.setItem('isVIP', 'true');
          }
        }
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  const checkVIPStatus = async (email) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/check-vip/${email}`);
      return response.data;
    } catch (error) {
      return { success: false, isVIP: false };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!user,
      isVIP: user?.isVIP || false,
      login,
      register,
      logout,
      refreshUser,
      checkVIPStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
